import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { usePrefersReducedMotion } from '@/hooks/common/use-reduced-motion';
import type { TourMedia as TourMediaType } from '../types';

interface Props {
  media: TourMediaType;
  /**
   * Optional next-step media. When the next step is a video, we inject a
   * `<link rel="preload" as="video">` once on mount so the user does not
   * stall when advancing. Image preload is skipped — `<img loading="lazy">`
   * + standard browser caching is sufficient and `preload` for images has
   * no consistent perf benefit.
   */
  preloadNext?: TourMediaType;
  className?: string;
}

const aspectClassFor = (
  aspect: '16:9' | '4:3' | '3:4' | '9:16' | 'auto' | undefined,
): string => {
  if (aspect === '4:3') return 'aspect-[4/3]';
  if (aspect === '3:4') return 'aspect-[3/4]';
  if (aspect === '9:16') return 'aspect-[9/16]';
  if (aspect === 'auto') return '';
  // Default — both image and video opt into 16:9 unless the consumer asked
  // otherwise.
  return 'aspect-[16/9]';
};

const PRELOAD_DATA_ATTR = 'data-tour-preload';

/**
 * Inject a single `<link rel="preload" as="video">` for the given URL when
 * the consumer is currently displaying a different step. Idempotent — the
 * URL acts as the dedupe key, so multiple TourMedia mounts pointing at the
 * same upcoming asset do not pile up extra link elements.
 */
const ensurePreloadLink = (url: string): HTMLLinkElement | null => {
  if (typeof document === 'undefined') return null;
  const existing = document.head.querySelector<HTMLLinkElement>(
    `link[${PRELOAD_DATA_ATTR}="${CSS.escape(url)}"]`,
  );
  if (existing) return existing;
  const link = document.createElement('link');
  // Use setAttribute for `as` because jsdom does not reflect the IDL `as`
  // property to the attribute. Real browsers handle either path; this keeps
  // the unit tests honest.
  link.setAttribute('rel', 'preload');
  link.setAttribute('as', 'video');
  link.setAttribute('href', url);
  link.setAttribute(PRELOAD_DATA_ATTR, url);
  document.head.appendChild(link);
  return link;
};

/**
 * Single rendering surface for tour media. Handles image / video / none
 * with a fixed aspect-ratio wrapper so the card height does not reflow
 * when the media loads.
 *
 * Reduced-motion behavior: when the OS asks for reduced motion, video
 * media renders only the poster as an `<img>` — no autoplay, no source
 * children, no implicit network IO for the clip itself. The aspect-locked
 * slot still reserves the same space.
 */
export const TourMedia: React.FC<Props> = ({ media, preloadNext, className }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [videoErrored, setVideoErrored] = useState(false);

  // Preload-next: inject a `<link rel="preload">` on mount and remove it
  // on unmount. We key the cleanup on the URL via a data attribute so a
  // remount on the same step does not double up.
  useEffect(() => {
    if (!preloadNext || preloadNext.type !== 'video') return;
    const url = preloadNext.sources[0]?.src;
    if (!url) return;
    const link = ensurePreloadLink(url);
    return () => {
      // Only remove the element we created; if a sibling component is also
      // referencing it (rare), the data-attribute lookup would still find
      // it, which is fine — the removal runs once per consumer.
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [preloadNext]);

  // Reset the video-error flag whenever the media changes — otherwise a
  // failed video on step N would keep the poster fallback active when the
  // user advances to a perfectly-loadable video on step N+1.
  useEffect(() => {
    setVideoErrored(false);
  }, [media]);

  if (media.type === 'none') {
    return null;
  }

  if (media.type === 'image') {
    const aspectCls = aspectClassFor(media.aspect ?? '16:9');
    return (
      <div className={clsx('overflow-hidden rounded-md ring-1 ring-gray-200', aspectCls, className)}>
        <img
          src={media.src}
          srcSet={media.src2x ? `${media.src} 1x, ${media.src2x} 2x` : undefined}
          alt={media.alt}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  // media.type === 'video'
  const aspectCls = aspectClassFor(media.aspect ?? '16:9');

  // Reduced-motion or a video error → render the poster as a static <img>.
  // The poster URL is mandatory on video media so this fallback is always
  // viable.
  if (prefersReducedMotion || videoErrored) {
    return (
      <div className={clsx('overflow-hidden rounded-md ring-1 ring-gray-200', aspectCls, className)}>
        <img
          src={media.poster}
          alt={media.alt}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  // Keying the <video> on the first source's URL forces React to unmount and
  // remount the element when the resolved media changes. Browsers don't pick
  // up new <source> children on an existing <video> without a manual
  // load() — remounting sidesteps that, and the poster is shown until the
  // new sources buffer.
  const videoKey = media.sources[0]?.src ?? media.poster;

  return (
    <div className={clsx('overflow-hidden rounded-md ring-1 ring-gray-200', aspectCls, className)}>
      <video
        key={videoKey}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        poster={media.poster}
        aria-label={media.alt}
        onError={() => setVideoErrored(true)}
        className="h-full w-full object-cover"
      >
        {media.sources.map((s) => (
          <source key={s.src} src={s.src} type={s.mime} />
        ))}
      </video>
    </div>
  );
};
